import React from 'react';
import { Link, NavLink, useLocation } from 'react-router-dom';

interface PublicAuthLayoutProps {
  eyebrow: string;
  title: string;
  subtitle: string;
  children: React.ReactNode;
  asideTitle?: string;
  asideBody?: string;
  highlights?: string[];
  footer?: React.ReactNode;
}

export default function PublicAuthLayout({
  asideBody = 'Seedronics Cloud brings site monitoring, asset visibility, energy, utilities, alerts, reports, and maintenance signals into one secure workspace.',
  asideTitle = 'Run your facilities from anywhere.',
  children,
  eyebrow,
  footer,
  highlights = ['Live site visibility', 'Flexible data analysis', 'Predictive maintenance support'],
  subtitle,
  title,
}: PublicAuthLayoutProps) {
  const { pathname } = useLocation();
  const cta = pathname === '/login'
    ? { label: 'Start free trial', to: '/signup' }
    : { label: 'Open Console', to: '/login' };

  return (
    <div className="sd-public-auth">
      <header className="sd-public-auth-nav">
        <Link className="sd-public-auth-brand" to="/" aria-label="Seedronics home">
          <img src="/brand/seedronics-wordmark-light.png" alt="Seedronics" />
        </Link>
        <nav aria-label="Public navigation">
          <NavLink to="/use-cases">Use cases</NavLink>
          <NavLink to="/pricing">Pricing</NavLink>
          <NavLink to="/faq">FAQ</NavLink>
          <NavLink to="/contact">Contact us</NavLink>
        </nav>
        <Link className="sd-public-auth-cta" to={cta.to}>
          {cta.label}
          <ArrowRightIcon />
        </Link>
      </header>

      <main className="sd-public-auth-shell">
        <section className="sd-public-auth-copy">
          <p>{eyebrow}</p>
          <h1>{title}</h1>
          <span>{subtitle}</span>
          <div className="sd-public-auth-proof">
            {highlights.map((item) => (
              <strong key={item}>{item}</strong>
            ))}
          </div>
        </section>

        <section className="sd-public-auth-card" aria-label={title}>
          <div className="sd-public-auth-card-header">
            <span>{asideTitle}</span>
            <p>{asideBody}</p>
          </div>
          {children}
          {footer && <div className="sd-public-auth-footer">{footer}</div>}
        </section>
      </main>
    </div>
  );
}

function ArrowRightIcon() {
  return (
    <svg aria-hidden="true" viewBox="0 0 24 24">
      <path d="M5 12h14" />
      <path d="m13 6 6 6-6 6" />
    </svg>
  );
}

export function PublicAuthAlert({
  children,
  tone = 'error',
}: {
  children: React.ReactNode;
  tone?: 'error' | 'success' | 'warning';
}) {
  return (
    <div className={`sd-public-auth-alert is-${tone}`}>
      {children}
    </div>
  );
}

export function PublicAuthSpinner() {
  return <span className="sd-public-auth-spinner" aria-hidden="true" />;
}
